'use client';
import { Button, Checkbox, Dialog, DialogBody } from '@material-tailwind/react';
import {
  PostApply_LeadResendEmailOperation,
  RewardProduct_Detail,
} from '__generated__/graphql';
import { GoogleIcon } from 'components/modulab/Icon';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useClient } from 'hooks/useClient';
import { useMutation } from 'hooks/useQuery';
import { usePathname } from 'next/navigation';
import { FormEvent, FormEventHandler, JSX, useEffect, useState } from 'react';
import { trackLeadCreation } from 'utils/serverTracking';
import { Translated } from 'utils/staticText';
import {
  trackingAuthButtonClick,
  trackingMembershipRewards,
} from 'utils/tracking';
import {
  createLead,
  SendLeadToServiceBus,
} from '$/(application)/apply/[productId]/server-actions';
import { StickyLoginStaticText } from '$/(application)/i18n';
import { LoaderForm, SubmitButton } from '$/components/LoaderForm';
import { StickyFooter } from '$/components/StickyFooter';
import { useGoogleLogin } from '$/modules/auth/hooks';
import { parseForm } from '$/modules/form';
import { AlphaTextInput } from '$/modules/material-tailwind/AlphaTextInput';

type Props = {
  productId: number;
  authEndpoint: string;
  trackLeadCreation: typeof trackLeadCreation;
  createLead: typeof createLead;
  staticText: Translated<typeof StickyLoginStaticText>;
  product: RewardProduct_Detail;
  redirectUrl: string;
};

type Committed = {
  email: string;
  leadId?: number;
};

export function StickyLogin(props: Props): JSX.Element {
  const { staticText } = props;
  const [committed, setCommitted] = useState<Committed | null>(null);

  const [isInAppBrowser, setIsInAppBrowser] = useState(false);

  useEffect(() => {
    const userAgent = navigator.userAgent || '';
    const isFacebookApp =
      userAgent.includes('FBAN') || userAgent.includes('FBAV');
    const isInstagramApp = userAgent.includes('Instagram');

    setIsInAppBrowser(isFacebookApp || isInstagramApp);
  }, []);

  const googleLogin = useGoogleLogin(props.authEndpoint);
  const resendEmailMutation = useMutation(PostApply_LeadResendEmailOperation);
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const entries = parseForm(event.currentTarget);
    const email = String(entries.email);

    if (committed?.email == null) window.open(props.redirectUrl);
    if (committed?.email === email && committed.leadId != null) {
      trackingMembershipRewards('Resend Email', 'start-of-application', 2, {
        campaignId: props.productId,
        campaignName: props.product.productName,
        email: email,
        leadId: committed.leadId,
        product: {
          productUid: props.product?.catalogueProduct?.productUid,
          productName: props.product?.catalogueProduct?.productName,
          providerName: props.product?.catalogueProduct?.provider?.providerName,
        },
      });
      await resendEmailMutation.send({
        input: {
          email,
          type: 'EMAIL_SUBMISSION',
          leadId: committed.leadId,
        },
      });
    } else {
      const requestSource = ['MEMBERSHIP'];
      const experimentId = getMetaData('experiment_id');
      const variantId = getMetaData('variant_id');
      if (experimentId && variantId) {
        requestSource.push(`exp-${experimentId}`);
        requestSource.push(`var-${variantId}`);
        requestSource.push(`met-email`);
      }

      setCommitted({ email });
      trackingAuthButtonClick(
        'Continue with Email',
        'email',
        `${document.title}_${staticText.continueWithEmail}`,
        Boolean(entries.consent),
      );
      const result = await createLead({
        email,
        productId: props.productId,
        consent: Boolean(entries.consent),
        searchParams: location.search,
        requestSource: requestSource.join(','),
      });
      await SendLeadToServiceBus({
        Email: email,
        Last_Name: email ?? '', // Last name is required, so we use email as a placeholder
        Product_Type: props?.product?.productVertical?.name ?? '',
        Marketing_Consent: Boolean(entries.marketingConsent),
        Selected_Product: props.product?.catalogueProduct?.productName ?? '',
        Selected_Provider:
          props.product?.catalogueProduct?.provider?.providerName ?? '',
      });
      setCommitted({ leadId: result.leadId, email });
      if (props.product.productId) {
        trackLeadCreation({
          email: email,
          vertical: props.product.productVertical?.code ?? '',
          item_brand:
            props?.product.catalogueProduct?.provider?.providerName ?? '',
          item_id: props.product.catalogueProduct?.productUid ?? '',
          item_name: props.product.catalogueProduct?.productName ?? '',
          reward_lead_id: result.leadId,
          reward_campaign_name: props.product.productName ?? '',
          reward_campaign_id: props.product.productId,
          login_method: 'email',
        });
      }
    }
  };

  const pathname = usePathname();
  const getGoogleRedirectUrl = useClient(() => {
    const urlParams = new URLSearchParams(location.search ?? '');
    const source = urlParams.get('source');
    const baseUrl = `${pathname?.split('/').slice(0, -1).join('/')}`;
    return source
      ? `${baseUrl}?action=google&source=${source}`
      : `${baseUrl}?action=google`;
  });
  return (
    <>
      <StickyFooter>
        <LoaderForm
          className="border-t border-t-gray-300 bg-white"
          onSubmit={onSubmit}
        >
          <input
            type="hidden"
            name="redirect_uri"
            value={getGoogleRedirectUrl}
          />
          <div className="mx-auto flex max-w-screen-md flex-col gap-2 p-4 md:items-center md:gap-4 md:p-6 lg:max-w-page-max">
            <div className="hidden font-semibold text-gray-900 text-lg md:block">
              {staticText.stickyLoginHeader}
            </div>
            <div
              // In the design, the "Continue with Google" and input field have the same width, which is implemented here
              // using a grid and giving both the same basis.
              className="grid auto-cols-min grid-cols-1 items-center gap-2 self-stretch md:flex"
            >
              {!isInAppBrowser && (
                <>
                  <div className="col-span-2 grow basis-1">
                    <Button
                      variant="outlined"
                      fullWidth
                      className="inline-flex items-center gap-2"
                      onClick={googleLogin}
                    >
                      <GoogleIcon size={3} />
                      <div className="capsize">
                        {staticText.continueWithGoogle}
                      </div>
                    </Button>
                  </div>
                  <div className="col-span-2 justify-self-center font-normal text-gray-600 text-base md:font-medium md:text-gray-700 md:text-lg">
                    {staticText.or}
                  </div>
                </>
              )}
              <div className="grow basis-1">
                <AlphaTextInput
                  name="email"
                  type="email"
                  placeholder={staticText.enterYourEmail}
                  required
                  hideValidation
                  onValidate={(state) => {
                    if (!state.validity.valid) {
                      return staticText.incorrectEmail;
                    }
                  }}
                />
              </div>
              <div className="col-start-2">
                <SubmitButton
                  color="primary"
                  className="inline-flex items-center px-2 md:px-4"
                >
                  <MaterialIcon
                    icon="arrow_right_alt"
                    size={3}
                    className="md:!hidden"
                  />
                  <div className="hidden md:block">
                    {staticText.continueWithEmail}
                  </div>
                </SubmitButton>
              </div>
            </div>
            <div>
              <div className="-ml-3 -mt-3">
                <Checkbox
                  name="consent"
                  value="true"
                  labelProps={{ className: 'text-xs font-medium' }}
                  label={staticText.marketingConsent}
                />
              </div>
            </div>
            <div className="self-stretch border-t border-gray-300 pt-2 text-center font-medium text-gray-600 text-xs">
              {staticText.loginTermsAndConditions}
            </div>
          </div>
        </LoaderForm>
      </StickyFooter>
      {committed && props.redirectUrl ? (
        <BackupPlanDialog
          staticText={staticText}
          onSubmit={onSubmit}
          email={committed.email}
          providerUri={props.redirectUrl}
        />
      ) : null}
    </>
  );
}

function BackupPlanDialog(props: {
  staticText: Translated<typeof StickyLoginStaticText>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  email: string | undefined;
  providerUri: string;
}) {
  const { staticText } = props;
  return (
    <Dialog open handler={() => {}} size="xs">
      <LoaderForm onSubmit={props.onSubmit}>
        <DialogBody className="flex flex-col gap-4">
          <div className="self-center text-gray-900 alpha-title-1">
            {staticText.didntGetClaimEmail}
          </div>
          <div>
            <AlphaTextInput
              name="email"
              type="email"
              required
              defaultValue={props.email}
            />
          </div>
          <div>
            <SubmitButton color="primary" fullWidth>
              {staticText.resendEmail}
            </SubmitButton>
          </div>
          <div className="self-center text-gray-700 alpha-body-1">
            {staticText.or}
          </div>
          <div className="self-center text-gray-900 alpha-title-1">
            {staticText.didntCompleteApplication}
          </div>
          <div>
            <a href={props.providerUri} target="_blank" rel="noreferrer">
              <Button
                variant="outlined"
                fullWidth
                className="flex items-center justify-center gap-1 px-1"
              >
                {staticText.restartApplication}
                <MaterialIcon icon="open_in_new" size={3} />
              </Button>
            </a>
          </div>
        </DialogBody>
      </LoaderForm>
    </Dialog>
  );
}

function getMetaData(name: string) {
  const metaTag = document.querySelector(`meta[name="${name}"]`);
  return metaTag ? metaTag.getAttribute('content') : '';
}
